import React, { useState } from 'react';

import { JaaSMeeting } from '@jitsi/react-sdk';

import { Box } from '@mui/material';

import Button from 'components/Button';
import Spin from 'components/Spin';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { MeetingRoom as IMeetingRoom } from 'pages/MeetingRoom/types/meetingTypes';
import { apiMeeting } from 'services/apiService';

interface JitsiVideoEmbedMobileProps {
  meetingRoom: IMeetingRoom;
  onClose?: () => void;
}

interface IJitsiAuth {
  app_id: string;
  token: string;
}

export const JitsiVideoEmbedMobile: React.FC<JitsiVideoEmbedMobileProps> = ({
  meetingRoom,
  onClose,
}) => {
  const { toastError } = useToast();
  const [jitsiAuth, setJitsiAuth] = useState<IJitsiAuth>();
  const [error, setError] = useState<boolean>(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showJitsi, setShowJitsi] = useState(false);
  const { user } = useUser();

  const getToken = async () => {
    try {
      const response = await apiMeeting.get(`/v1/integrations/jitsi/token?user_id=${user?.id}`);
      setJitsiAuth(response?.data ?? '');
      setVideoLoading(false);
    } catch (error) {
      setError(true);
      toastError('Algo deu errado ao carregar o vídeo');
      console.error('VIDEOCONF', { error });
    }
  };

  const handleEnterMeeting = () => {
    setShowMeetingModal(true);
    getToken();
  };

  const handleCloseMeetingModal = () => {
    setShowMeetingModal(false);
    onClose?.();
  };

  const handleConfirmEnterMeeting = () => {
    setShowMeetingModal(false);
    setShowJitsi(true);
  };

  const handleCloseJitsi = () => {
    setShowJitsi(false);
    onClose?.();
  };

  const renderJitsi = () => {
    if ((videoLoading && !error) || !jitsiAuth) {
      return <Spin centerHorizontally centerVertically />;
    }

    if (error || (!videoLoading && !jitsiAuth)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button color="error" onClick={getToken}>
            Tentar novamente
          </Button>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 2000,
          bgcolor: 'black',
        }}
      >
        <JaaSMeeting
          appId={jitsiAuth?.app_id}
          roomName={meetingRoom.room_data.title}
          jwt={jitsiAuth?.token}
          configOverwrite={{
            disableLocalVideoFlip: true,
            backgroundAlpha: 0,
            defaultLanguage: 'pt-BR',
            toolbarConfig: {
              timeout: 2000,
            },
            disableDeepLinking: true,
            disableP2P: true,
            enableClosePage: true,
            enableWelcomePage: false,
            prejoinPageEnabled: false,
            startWithAudioMuted: true,
            startWithVideoMuted: true,
          }}
          interfaceConfigOverwrite={{
            VIDEO_LAYOUT_FIT: 'nocrop',
            MOBILE_APP_PROMO: false,
            TILE_VIEW_MAX_COLUMNS: 2,
            TOOLBAR_BUTTONS: [
              'microphone',
              'camera',
              'closedcaptions',
              'desktop',
              'fullscreen',
              'fodeviceselection',
              'hangup',
              'profile',
              'chat',
              'recording',
              'livestreaming',
              'etherpad',
              'sharedvideo',
              'settings',
              'raisehand',
              'videoquality',
              'filmstrip',
              'feedback',
              'stats',
              'shortcuts',
              'tileview',
              'select-background',
              'download',
              'help',
              'mute-everyone',
            ],
          }}
          userInfo={{
            displayName: `${user?.first_name} ${user?.last_name}`,
            email: user?.email ?? '',
          }}
          spinner={Spin}
          onApiReady={externalApi => {
            console.log('Jitsi API Ready', { externalApi });
            setVideoLoading(false);
          }}
          onReadyToClose={handleCloseJitsi}
        />
      </Box>
    );
  };

  return (
    <Box
      justifyContent="center"
      alignItems="center"
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      sx={{ pb: !showJitsi ? '72px' : 0 }}
    >
      {/* Botão fixo no footer */}
      {!showJitsi && (
        <Box
          className="mobile-fixed-bottom"
          sx={{
            position: 'fixed',
            left: 0,
            width: '100vw',
            zIndex: 1200,
            p: 2,
            bgcolor: 'transparent',
          }}
        >
          <Button
            color="primary"
            fullWidth
            onClick={handleEnterMeeting}
            disabled={!meetingRoom.participation_status.is_participant}
            style={{ fontWeight: 'bold', fontSize: '1.1rem' }}
          >
            Entrar na Reunião
          </Button>
        </Box>
      )}

      <GlobalModal
        open={showMeetingModal}
        title="Entrar na Reunião"
        description={`Você está prestes a entrar na sala "${meetingRoom.room_data.title}". Deseja continuar?`}
        onClose={handleCloseMeetingModal}
        onCancel={handleCloseMeetingModal}
        onConfirm={handleConfirmEnterMeeting}
        confirmButtonText="Entrar"
        cancelButtonText="Cancelar"
        closeOnBackdropClick={false}
        width="90%"
        height="auto"
      />

      {/* Player em tela cheia */}
      {showJitsi && renderJitsi()}
    </Box>
  );
};

export default JitsiVideoEmbedMobile;
